import type {
  FeatureRecord,
  GapRecord,
  ReadinessGroup,
} from '../types/platform'

/**
 * THE FEATURE STATUS REGISTER.
 *
 * Every capability in Campuzon appears here exactly once with its real status.
 * Surfaces read this rather than hard-coding "coming soon" copy, so a
 * capability cannot look operational on one screen and unavailable on another.
 */
export const featureRegister: FeatureRecord[] = [
  /* ---------------------------------------------------------------- power */
  {
    key: 'power_reports',
    label: 'Community power reporting',
    domain: 'Power',
    status: 'live',
    surface: '/power',
    dependency: 'none',
    note: 'Reports are aggregated by evidence source and decayed by age. Rate limits and duplicate detection are enforced on submission.',
  },
  {
    key: 'power_meter',
    label: 'Connected energy monitor',
    domain: 'Power',
    status: 'configured',
    surface: 'Property console → Power',
    dependency: 'engineering',
    note: 'The evidence model already carries a connected-meter source, so a device feed would appear alongside community reports with its provenance stated. No device integration is built yet, and nothing on Campuzon claims a meter reading today.',
  },
  {
    key: 'power_notifications',
    label: 'Power restored alerts',
    domain: 'Power',
    status: 'configured',
    surface: '/power',
    dependency: 'administrator_configuration',
    note: 'Subscriptions are recorded and delivered through existing notification preferences. Cooldowns and deduplication need configuring per campus before alerts are sent.',
  },

  /* ------------------------------------------------------------- identity */
  {
    key: 'registration',
    label: 'Account creation',
    domain: 'Identity',
    status: 'live',
    surface: '/register',
    dependency: 'none',
    note: 'Email and password. Consent version is recorded on the account.',
  },
  {
    key: 'email_verification',
    label: 'Email verification',
    domain: 'Identity',
    status: 'live',
    surface: '/verify-email',
    dependency: 'none',
    note: 'Single-use expiring code with attempt limits and a resend cooldown.',
  },
  {
    key: 'phone_verification',
    label: 'Phone verification',
    domain: 'Identity',
    status: 'configured',
    surface: 'Settings → Security',
    dependency: 'licensed_provider',
    note: 'The OTP flow is built. It needs an SMS gateway agreement before it can send.',
  },
  {
    key: 'oauth_google',
    label: 'Sign in with Google',
    domain: 'Identity',
    status: 'disabled',
    surface: '/signin',
    dependency: 'administrator_configuration',
    note: 'No OAuth client is configured, so the option is not offered at all.',
  },
  {
    key: 'passkeys',
    label: 'Passkeys',
    domain: 'Identity',
    status: 'under_development',
    surface: 'Settings → Security',
    dependency: 'engineering',
    note: 'Not production-ready. Not offered anywhere in the interface.',
  },
  {
    key: 'mfa_totp',
    label: 'Authenticator app (MFA)',
    domain: 'Identity',
    status: 'staged',
    surface: 'Settings → Security',
    dependency: 'engineering',
    note: 'Enrolment and recovery codes work in staging. Not enabled in production yet.',
  },
  {
    key: 'sessions',
    label: 'Session management',
    domain: 'Identity',
    status: 'live',
    surface: 'Settings → Security',
    dependency: 'none',
    note: 'Active sessions can be reviewed and revoked individually or all at once.',
  },
  {
    key: 'recovery',
    label: 'Account recovery',
    domain: 'Identity',
    status: 'live',
    surface: '/forgot-password',
    dependency: 'none',
    note: 'Expiring single-use code. Completing a reset signs out every other session.',
  },
  {
    key: 'username_system',
    label: 'Campuzon username',
    domain: 'Identity',
    status: 'live',
    surface: 'Onboarding, Settings → Profile',
    dependency: 'none',
    note: 'Unique, case-normalised, reserved-name protected, changeable every 30 days.',
  },
  {
    key: 'role_switching',
    label: 'Multiple roles on one account',
    domain: 'Identity',
    status: 'live',
    surface: 'Console switcher',
    dependency: 'none',
    note: 'Only consoles the identity holds a role for are offered.',
  },

  /* ----------------------------------------------------- student lifecycle */
  {
    key: 'admissions',
    label: 'University, programme and scholarship discovery',
    domain: 'Student lifecycle',
    status: 'live',
    surface: '/discover',
    dependency: 'none',
    note: 'Catalogue entries carry a source and a last-updated date.',
  },
  {
    key: 'applications',
    label: 'Application workspace',
    domain: 'Student lifecycle',
    status: 'live',
    surface: '/discover',
    dependency: 'none',
    note: 'Requirements, documents, readiness and correspondence per application.',
  },
  {
    key: 'academics',
    label: 'Academic management',
    domain: 'Student lifecycle',
    status: 'configured',
    surface: '/academics',
    dependency: 'institutional_agreement',
    note: 'Registration, timetable, results and attendance need a data agreement per institution.',
  },
  {
    key: 'learning',
    label: 'Learning workspace',
    domain: 'Student lifecycle',
    status: 'live',
    surface: '/academics → Learning',
    dependency: 'none',
    note: 'Progress is platform-derived and never presented as a university grade.',
  },
  {
    key: 'notes',
    label: 'Campuzon Notes',
    domain: 'Student lifecycle',
    status: 'live',
    surface: '/notes',
    dependency: 'none',
    note: 'Private by default. Diary entries are never visible to institutions.',
  },
  {
    key: 'notes_realtime',
    label: 'Real-time note collaboration',
    domain: 'Student lifecycle',
    status: 'staged',
    surface: '/notes',
    dependency: 'engineering',
    note: 'Presence and session control exist. Concurrent editing is not enabled in production.',
  },

  /* ---------------------------------------------------------------- housing */
  {
    key: 'housing_search',
    label: 'Accommodation search',
    domain: 'Housing',
    status: 'live',
    surface: '/housing',
    dependency: 'none',
    note: 'Verified and not-inspected listings are distinguished on every card.',
  },
  {
    key: 'housing_application',
    label: 'Housing application',
    domain: 'Housing',
    status: 'live',
    surface: '/housing/:listing',
    dependency: 'none',
    note: 'Three-step application. Payment of rent is a separate, gated capability.',
  },
  {
    key: 'roommates',
    label: 'Roommate matching',
    domain: 'Housing',
    status: 'live',
    surface: '/housing/roommates',
    dependency: 'none',
    note: 'Overlap is computed from stated preferences with disclosure controls.',
  },
  {
    key: 'rent_payment',
    label: 'Rent payment',
    domain: 'Housing',
    status: 'configured',
    surface: '/housing/:listing',
    dependency: 'licensed_provider',
    note: 'Ledger and escrow are built. Collecting rent needs a licensed payment institution.',
  },

  /* ------------------------------------------------------------ marketplace */
  {
    key: 'marketplace',
    label: 'Marketplace engine',
    domain: 'Marketplace',
    status: 'live',
    surface: '/marketplace',
    dependency: 'none',
    note: 'One engine behind knowledge, services, mentorship, events and housing.',
  },
  {
    key: 'escrow',
    label: 'Marketplace escrow',
    domain: 'Marketplace',
    status: 'configured',
    surface: '/marketplace → Your orders',
    dependency: 'licensed_provider',
    note: 'The full state machine runs. Holding buyer funds needs a licensed provider.',
  },
  {
    key: 'entitlements',
    label: 'Library entitlements',
    domain: 'Marketplace',
    status: 'live',
    surface: '/marketplace → Knowledge',
    dependency: 'none',
    note: 'Licence terms and entitlements are kept as separate records.',
  },
  {
    key: 'payouts',
    label: 'Provider payouts',
    domain: 'Marketplace',
    status: 'configured',
    surface: '/provider',
    dependency: 'licensed_provider',
    note: 'Earnings, settlement rules and fees are computed. Disbursement is not enabled.',
  },

  /* ---------------------------------------------------------------- finance */
  {
    key: 'ledger',
    label: 'Double-entry ledger',
    domain: 'Finance',
    status: 'live',
    surface: '/money, /admin/finance',
    dependency: 'none',
    note: 'Immutable paired entries. Balances are derived, never stored.',
  },
  {
    key: 'account_funding',
    label: 'Account funding',
    domain: 'Finance',
    status: 'configured',
    surface: '/money',
    dependency: 'regulatory_approval',
    note: 'Campuzon does not hold customer funds. Awaiting licensing and a payment institution.',
  },
  {
    key: 'social_payments',
    label: 'Send, request and split',
    domain: 'Finance',
    status: 'configured',
    surface: '/money',
    dependency: 'regulatory_approval',
    note: 'Composition and ledger entries are built. Moving value is not enabled.',
  },
  {
    key: 'bills',
    label: 'Bills and airtime',
    domain: 'Finance',
    status: 'configured',
    surface: '/money → Bills',
    dependency: 'licensed_provider',
    note: 'Category → provider → product is configuration-driven. No biller is contracted yet.',
  },
  {
    key: 'reconciliation',
    label: 'Reconciliation',
    domain: 'Finance',
    status: 'live',
    surface: '/admin/finance',
    dependency: 'none',
    note: 'Ledger against instruments, redemptions and settlements, with drift shown.',
  },

  /* -------------------------------------------------------------- campagift */
  {
    key: 'campagift',
    label: 'CampaGift instruments',
    domain: 'CampaGift',
    status: 'live',
    surface: '/money/campagift',
    dependency: 'none',
    note: 'Issuance, rules, expiry, transfer and full history all operate.',
  },
  {
    key: 'campagift_purchase',
    label: 'CampaGift purchase',
    domain: 'CampaGift',
    status: 'configured',
    surface: '/money/campagift',
    dependency: 'regulatory_approval',
    note: 'Issuance only follows provider confirmation. Real-money purchase is gated.',
  },
  {
    key: 'campagift_redemption',
    label: 'Merchant redemption',
    domain: 'CampaGift',
    status: 'live',
    surface: '/merchant',
    dependency: 'none',
    note: 'Eligibility, partial redemption, caps and idempotency all enforced.',
  },
  {
    key: 'campagift_settlement',
    label: 'Merchant settlement',
    domain: 'CampaGift',
    status: 'configured',
    surface: '/merchant, /admin/campagift',
    dependency: 'licensed_provider',
    note: 'Receivables and settlement batches are computed. Disbursement is not enabled.',
  },
  {
    key: 'campagift_bulk',
    label: 'Bulk and corporate issuance',
    domain: 'CampaGift',
    status: 'disabled',
    surface: '/admin/campagift',
    dependency: 'administrator_configuration',
    note: 'Intentionally off until a corporate issuer agreement exists.',
  },

  /* ------------------------------------------------------------- community */
  {
    key: 'community',
    label: 'People, groups and events',
    domain: 'Community',
    status: 'live',
    surface: '/community',
    dependency: 'none',
    note: 'Profile fields render according to the visibility tier their owner chose.',
  },
  {
    key: 'messaging',
    label: 'Messaging',
    domain: 'Community',
    status: 'live',
    surface: '/community → Messages',
    dependency: 'none',
    note: 'One thread infrastructure with block, report and transaction context.',
  },

  /* ----------------------------------------------------------- opportunity */
  {
    key: 'opportunities',
    label: 'Jobs and internships',
    domain: 'Opportunities',
    status: 'live',
    surface: '/opportunities',
    dependency: 'none',
    note: 'Verified and unverified employers are clearly distinguished.',
  },
  {
    key: 'employer_console',
    label: 'Employer console',
    domain: 'Opportunities',
    status: 'live',
    surface: '/employer',
    dependency: 'none',
    note: 'Pipeline shows only what each candidate’s visibility permits.',
  },

  /* -------------------------------------------------------------- services */
  {
    key: 'academic_services',
    label: 'Academic services',
    domain: 'Services',
    status: 'live',
    surface: '/services',
    dependency: 'none',
    note: 'Fees are itemised by payee. Credential sharing is refused throughout.',
  },
  {
    key: 'institution_integration',
    label: 'Institutional service integration',
    domain: 'Services',
    status: 'configured',
    surface: '/services',
    dependency: 'institutional_agreement',
    note: 'Requesting directly through an institution needs that institution to agree.',
  },

  /* --------------------------------------------------------------- network */
  {
    key: 'representative_network',
    label: 'Campus representative network',
    domain: 'Network',
    status: 'live',
    surface: '/network, /representative',
    dependency: 'none',
    note: 'Application, onboarding, tasks, evidence, review and earnings all operate.',
  },
  {
    key: 'representative_payouts',
    label: 'Representative payouts',
    domain: 'Network',
    status: 'configured',
    surface: '/representative → Earnings',
    dependency: 'licensed_provider',
    note: 'Earnings ledger is authoritative. Payout execution is not enabled.',
  },
  {
    key: 'offline_capture',
    label: 'Offline evidence capture',
    domain: 'Network',
    status: 'live',
    surface: '/representative',
    dependency: 'device_capability',
    note: 'Queues on the device with visible sync state where the browser permits it.',
  },

  /* -------------------------------------------------------------------- ai */
  {
    key: 'assistant',
    label: 'Campuzon Assistant',
    domain: 'Intelligence',
    status: 'live',
    surface: 'Every console',
    dependency: 'none',
    note: 'Answers from authorised data only, and refuses rather than inventing.',
  },
  {
    key: 'assistant_actions',
    label: 'Assistant-initiated payments',
    domain: 'Intelligence',
    status: 'configured',
    surface: 'Assistant',
    dependency: 'regulatory_approval',
    note: 'Confirmation cards are produced but cannot execute a transfer.',
  },
  {
    key: 'recommendations',
    label: 'Recommendations',
    domain: 'Intelligence',
    status: 'live',
    surface: 'Discover, Opportunities, Community, Learning',
    dependency: 'none',
    note: 'Each suggestion shows its reason; signals can be switched off individually.',
  },
]

/** Grouped in the order the launch checklist should be worked through. */
export const readinessGroups: ReadinessGroup[] = [
  {
    label: 'Identity and access',
    description: 'The front door. Nothing else can be trusted until this is.',
    items: [
      { label: 'Registration', state: 'complete', note: 'Minimal fields, versioned consent recorded.' },
      { label: 'Login and logout', state: 'complete', note: 'Sign out invalidates the stored session.' },
      { label: 'Email verification', state: 'complete', note: 'Expiring single-use code.' },
      { label: 'Phone verification', state: 'blocked', note: 'Needs an SMS gateway agreement.' },
      { label: 'Username creation and change', state: 'complete', note: 'Reserved names protected.' },
      { label: 'Password reset', state: 'complete', note: 'Other sessions invalidated on reset.' },
      { label: 'Account recovery', state: 'complete', note: 'No enumeration through the recovery form.' },
      { label: 'Multi-factor authentication', state: 'partial', note: 'Enrolment staged, not enabled.' },
      { label: 'Session management', state: 'complete', note: 'Review and revoke, individually or all.' },
      { label: 'Role-based authorisation', state: 'complete', note: 'Consoles gated by held role.' },
    ],
  },
  {
    label: 'Core product',
    description: 'What a person actually does after signing in.',
    items: [
      { label: 'Public homepage', state: 'complete', note: 'Campus register drives what is shown.' },
      { label: 'Navigation', state: 'complete', note: 'Ten destinations, four plus More on mobile.' },
      { label: 'Unified search', state: 'complete', note: 'Respects permission and visibility tiers.' },
      { label: 'Personalised home', state: 'complete', note: 'Derived from real account state.' },
      { label: 'Profile and settings', state: 'complete', note: 'Seven settings sections.' },
      { label: 'Notifications', state: 'partial', note: 'In-app works; email and push need a sender.' },
      { label: 'Messaging', state: 'complete', note: 'Block, report and transaction context.' },
      { label: 'Support and reporting', state: 'complete', note: 'Report paths on every surface.' },
    ],
  },
  {
    label: 'Marketplace',
    description: 'One engine, many verticals.',
    items: [
      { label: 'Listings and search', state: 'complete', note: 'Shared across every vertical.' },
      { label: 'Orders and bookings', state: 'complete', note: 'Full state machine with alternatives.' },
      { label: 'Reviews and reputation', state: 'complete', note: 'Per-domain, not a single score.' },
      { label: 'Disputes', state: 'complete', note: 'Evidence, reviewer, decision, reason.' },
      { label: 'Fees', state: 'complete', note: 'Configurable and itemised before payment.' },
      { label: 'Payouts', state: 'blocked', note: 'Needs a licensed disbursement provider.' },
    ],
  },
  {
    label: 'Finance',
    description: 'Built in full, activated only where permitted.',
    items: [
      { label: 'Double-entry ledger', state: 'complete', note: 'Immutable, integer minor units.' },
      { label: 'Reconciliation', state: 'complete', note: 'Drift surfaced rather than hidden.' },
      { label: 'CampaGift instruments', state: 'complete', note: 'Rules, expiry, partial redemption.' },
      { label: 'Merchant redemption', state: 'complete', note: 'Idempotent, double-spend safe.' },
      { label: 'Account funding', state: 'blocked', note: 'Awaiting regulatory position.' },
      { label: 'Transfers and requests', state: 'blocked', note: 'Awaiting regulatory position.' },
      { label: 'Escrow', state: 'blocked', note: 'Holding funds needs a licensed provider.' },
      { label: 'Bills', state: 'blocked', note: 'No biller aggregator contracted.' },
    ],
  },
  {
    label: 'Trust and governance',
    description: 'Trust is infrastructure, not a badge.',
    items: [
      { label: 'Verification lifecycle', state: 'complete', note: 'Expiry and re-verification included.' },
      { label: 'Reporting and blocking', state: 'complete', note: 'On listings, people, jobs, events.' },
      { label: 'Moderation queues', state: 'complete', note: 'Attributed decisions with reasons.' },
      { label: 'Risk flags', state: 'complete', note: 'Route for human review, never auto-punish.' },
      { label: 'Audit logging', state: 'complete', note: 'Actor, action, from, to, reason.' },
    ],
  },
  {
    label: 'Infrastructure',
    description: 'Owned by engineering, outside this application.',
    items: [
      { label: 'Production database and migrations', state: 'not_started', note: 'Versioned migrations required.' },
      { label: 'Backups and restore testing', state: 'not_started', note: 'An untested backup does not count.' },
      { label: 'Monitoring and alerting', state: 'not_started', note: 'Financial failures must alert.' },
      { label: 'Error tracking', state: 'not_started', note: 'No stack trace should reach a user.' },
      { label: 'Secrets management', state: 'not_started', note: 'Per-environment credentials.' },
      { label: 'CI/CD and rollback', state: 'not_started', note: 'Rollback must be rehearsed.' },
      { label: 'Domain, SSL and email sender', state: 'not_started', note: 'Blocks email verification at scale.' },
    ],
  },
]

/** The audit classification the production directive asks to be produced first. */
export const gapAudit: GapRecord[] = [
  {
    area: 'Identity, community, marketplace engine, CampaGift instruments, notes, services, network, assistant',
    classification: 'complete',
    detail: 'Behaviour is implemented end to end and exercised by its own screens.',
  },
  {
    area: 'Notifications, multi-factor authentication, real-time collaboration',
    classification: 'partial',
    detail: 'In-app or staged behaviour works; the remaining channel or mode is not enabled.',
  },
  {
    area: 'Academic records, institutional service integration',
    classification: 'needs_configuration',
    detail: 'Requires a per-institution data agreement before real records can appear.',
  },
  {
    area: 'Funding, transfers, escrow, bills, payouts, settlement',
    classification: 'needs_provider',
    detail: 'Ledger and state machines are authoritative; movement of value needs a licensed provider.',
  },
  {
    area: 'Stored value, wallet presentation, CampaGift real-money purchase',
    classification: 'needs_approval',
    detail: 'Must not be enabled until the regulatory position is established in writing.',
  },
  {
    area: 'Database migrations, backups, monitoring, secrets, CI/CD',
    classification: 'needs_configuration',
    detail: 'Outside this application. No launch until each is provisioned and rehearsed.',
  },
]
